/**
 * Collections monitoring — one flat table of paths, with documents beneath.
 *
 * It was a tree beside a documents panel, which meant answering "what needs
 * migration?" was a matter of expanding nodes until you found one. Depth is
 * now an indent rather than a disclosure, so every collection is on screen and
 * the filters answer the question directly.
 *
 * Selecting a row still lists its documents with resolved audiences — that is
 * the only place a dangling group shows — but below the table rather than
 * beside it, so the page stays one column of sections.
 *
 * The row action on a path needing migration opens the repair rather than
 * performing it: see `MigrateDialog` for why one click is not available.
 *
 * The tenant comes from the route (`/orgs/:orgId`), so a support deep link
 * lands on the same view a teammate was looking at.
 */
import { listOrgCollections } from '@optum/api-client/endpoints/collections';
import type { CollectionStatus } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';

import { Button } from '../../ui/Button.js';
import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { Banner } from '../../components/feedback/Banner.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { qk } from '../../lib/query-keys.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { Band } from '../../ui/Band.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { Icon } from '../../ui/finixt.js';
import { CollectionDocumentsPanel } from './CollectionDocumentsPanel.js';
import { migrateHref } from './migrate-href.js';

type Filter = 'all' | CollectionStatus;

export function CollectionsPage() {
  const navigate = useNavigate();
  const orgId = useRequiredOrgId();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // The selected path is the open record: one click, one path, the record in
  // the panel and its documents below the table.
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const selectedPath =
    panel.current?.kind === 'collection' && panel.current.orgId === orgId ? panel.current.id : null;

  const query = useQuery({
    queryKey: qk.collections(orgId),
    queryFn: () => listOrgCollections(orgId),
  });

  const nodes = useMemo(() => query.data?.nodes ?? [], [query.data]);
  const counts = useMemo(
    () => ({
      all: nodes.length,
      ready: nodes.filter((n) => n.status === 'ready').length,
      processing: nodes.filter((n) => n.status === 'processing').length,
      needs_migration: nodes.filter((n) => n.status === 'needs_migration').length,
    }),
    [nodes],
  );
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return nodes.filter(
      (n) =>
        (filter === 'all' || n.status === filter) &&
        (needle === '' || n.path.toLowerCase().includes(needle)),
    );
  }, [nodes, filter, search]);

  const unfiled = query.data?.unfiled ?? 0;

  return (
    <ListShape
      title="Collections"
      subtitle="Hierarchical data collections and the documents inside them."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <ListToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Search paths…"
          filters={
            <ChipFilter<Filter>
              options={[
                { key: 'all', label: 'All', count: counts.all },
                { key: 'ready', label: 'Ready', count: counts.ready },
                // Processing is transient. A chip that is always zero teaches
                // an operator to stop reading the row of chips.
                ...(counts.processing > 0
                  ? [
                      {
                        key: 'processing' as const,
                        label: 'Processing',
                        count: counts.processing,
                      },
                    ]
                  : []),
                {
                  key: 'needs_migration',
                  label: 'Needs migration',
                  count: counts.needs_migration,
                },
              ]}
              value={filter}
              onChange={setFilter}
            />
          }
          bare
        />

        {query.isPending && <LoadingBlock />}
        {query.isError && (
          <ErrorBlock
            message={query.error instanceof Error ? query.error.message : 'Failed to load'}
          />
        )}

        {query.isSuccess && nodes.length === 0 && (
          <EmptyBlock>This organization has not ingested any collections.</EmptyBlock>
        )}

        {query.isSuccess && nodes.length > 0 && (
          <DataTable
            count={`${visible.length} of ${nodes.length} collections`}
            hint={
              counts.needs_migration > 0
                ? `${counts.needs_migration} need migration`
                : 'all reachable'
            }
            rows={visible}
            rowKey="path"
            selectedKey={selectedPath ?? undefined}
            onRowClick={(node) =>
              node.path === selectedPath
                ? panel.close()
                : panel.open({ kind: 'collection', id: node.path, orgId })
            }
            // A collection has no page; its two clicks toggled the panel shut,
            // so the double-click opens it again (Feature 87, D17).
            onRowOpenPage={(node) => openPage({ kind: 'collection', id: node.path, orgId })}
            columns={[
              {
                key: 'path',
                label: 'Path',
                strong: true,
                render: (node) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Icon
                      name="folder_open"
                      size={14}
                      style={{ flexShrink: 0, color: 'var(--text-faint)' }}
                    />
                    {/* Depth as an indent, not a disclosure. The hierarchy is
                        still legible and nothing hides behind a toggle. */}
                    <span
                      style={{
                        fontSize: 'var(--text-meta-lg)',
                        ...{ paddingLeft: `${node.depth * 14}px` },
                      }}
                      className="mono"
                    >
                      {node.path}
                    </span>
                  </span>
                ),
              },
              {
                key: 'doc_count',
                label: 'Documents',
                align: 'right',
                mono: true,
                render: (node) => node.doc_count.toLocaleString(),
              },
              {
                key: 'status',
                label: 'Status',
                render: (node) => <StatusBadge status={node.status} vocabulary="document" />,
              },
              {
                key: 'actions',
                label: 'Actions',
                align: 'right',
                stopRowClick: true,
                render: (node) =>
                  node.status === 'needs_migration' ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => navigate(migrateHref(orgId, node.path))}
                    >
                      Migrate
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}

        {/* These rows name no collection, so they can never be a row in a table
            of collections. The banner is the only place they can be acted on. */}
        {unfiled > 0 && (
          <Banner title={`${unfiled} documents carry no collection`}>
            <p>
              They were ingested before collections existed, so they belong to no path above and
              retrieval cannot see them. Repairing them assigns a collection and the groups allowed
              to read it.
            </p>
            <div style={{ marginTop: 11 }}>
              <Button size="sm" variant="primary" onClick={() => navigate(migrateHref(orgId, ''))}>
                Migrate unfiled documents
              </Button>
            </div>
          </Banner>
        )}

        {selectedPath && (
          <>
            <Band
              label="Documents"
              meta={selectedPath}
              style={{ marginTop: 7, marginBottom: 11 }}
            />
            <p
              style={{ marginTop: -5, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}
            >
              Group chips show who can read each document; struck-through entries match no group in
              this organization.
            </p>
            <CollectionDocumentsPanel organizationId={orgId} path={selectedPath} />
          </>
        )}
      </div>
    </ListShape>
  );
}
